export const BUSINESS = {
  legalName: "Lab2o",
  tradeName: "Lab2o",
  email: "orders@lab2o.com",
};

export const POLICY_LINKS = [
  { slug: "shipping", label: "Shipping Policy" },
  { slug: "refunds", label: "Refund Policy" },
  { slug: "compliance", label: "Compliance & Disclaimer" },
  { slug: "research-use", label: "Research Use Policy" },
  { slug: "terms", label: "Terms of Service" },
  { slug: "privacy", label: "Privacy Policy" },
];

export const POLICIES = {
  shipping: {
    title: "Shipping Policy",
    updated: "August 2026",
    sections: [
      {
        heading: "Processing Time",
        body: "Orders are processed within 1–2 business days. Orders placed on weekends or holidays are processed the next business day. You will receive an email confirmation when your order ships.",
      },
      {
        heading: "Shipping Area",
        body: "We ship within the continental United States only. We do not ship to Alaska, Hawaii, Puerto Rico, US territories, PO boxes, or any international destination. Orders with shipping addresses outside the continental US will be cancelled and refunded.",
      },
      {
        heading: "Shipping Methods & Rates",
        body: "Standard Ground (5–7 business days): $6.99. Expedited (2–3 business days): $14.99. Orders with a merchandise subtotal of $150.00 or more qualify for free Standard Ground shipping. Rates are calculated and displayed at checkout before payment.",
      },
      {
        heading: "Packaging",
        body: "All vials are shipped in protective, contamination-resistant packaging with tamper-evident seals. Vials ship unlabeled, consistent with their status as research reagents.",
      },
      {
        heading: "Tracking",
        body: "A tracking number is provided by email when your order is dispatched. You can use this number on the carrier's website to follow your shipment.",
      },
      {
        heading: "Lost or Damaged Shipments",
        body: "Risk of loss passes to the carrier upon dispatch. If your shipment arrives damaged or does not arrive, contact us at orders@lab2o.com within 7 days of the expected delivery date and we will work with you to replace or refund the order.",
      },
    ],
  },
  refunds: {
    title: "Refund Policy",
    updated: "August 2026",
    sections: [
      {
        heading: "Return Window",
        body: "Unopened, unused products in their original sealed packaging may be returned within 14 days of delivery for a refund of the product price. Because our products are sterile research reagents, opened or unsealed vials and boxes are not eligible for return under any circumstances.",
      },
      {
        heading: "Damaged or Incorrect Orders",
        body: "If your order arrives damaged, defective, or incorrect, contact us at orders@lab2o.com within 7 days of delivery with your order reference and photos of the issue. We will replace the affected items or issue a full refund, including shipping, at no cost to you.",
      },
      {
        heading: "How to Request a Return",
        body: "Email orders@lab2o.com with your order reference and the reason for the return. We will respond within one business day with return instructions. Do not ship items back without authorization; unauthorized returns cannot be processed.",
      },
      {
        heading: "Refund Timing",
        body: "Approved refunds are issued to the original payment method within 5–10 business days of receiving the returned items or approving the claim. Original shipping charges are non-refundable except in cases of our error.",
      },
      {
        heading: "Cancellations",
        body: "Orders may be cancelled for a full refund any time before dispatch. Once an order has shipped, the standard return process above applies.",
      },
    ],
  },
  compliance: {
    title: "Compliance & Disclaimer",
    updated: "August 2026",
    sections: [
      {
        heading: "Research Purposes Only",
        body: "All products sold by Lab2o are supplied strictly as laboratory research reagents. They are intended solely for research purposes. They are not for human or veterinary use or consumption of any kind.",
      },
      {
        heading: "No Medical Claims",
        body: "No medical, therapeutic, diagnostic, clinical, or health-related claims are made or implied regarding any Lab2o product. Our products are not drugs, medications, medical devices, dietary supplements, or cosmetics, and they have not been evaluated or approved by the US Food and Drug Administration for any such use.",
      },
      {
        heading: "Buyer Responsibility",
        body: "The buyer assumes all responsibility for the safe handling, storage, use, and disposal of products in accordance with all applicable federal, state, and local laws, regulations, and institutional guidelines. The buyer represents that products will be used only by qualified personnel in an appropriate laboratory research setting.",
      },
      {
        heading: "Limitation of Liability",
        body: "Lab2o shall not be liable for any damages arising from the misuse of products, use of products contrary to this disclaimer, or use of products for any purpose other than legitimate laboratory research.",
      },
      {
        heading: "Right to Refuse Sale",
        body: "We reserve the right to refuse or cancel any order, at our sole discretion, where there is reason to believe the products may be used for any purpose other than lawful laboratory research.",
      },
    ],
  },
  "research-use": {
    title: "Research Use Policy",
    updated: "August 2026",
    sections: [
      {
        heading: "Intended Use",
        body: "Lab2o products are bacteriostatic water research reagents sold exclusively for use in laboratory research. Any other use — including but not limited to human or veterinary administration, compounding, or consumption — is strictly prohibited and contrary to the conditions of sale.",
      },
      {
        heading: "Qualified Buyers",
        body: "Products are offered only to purchasers who are at least 18 years of age and who will use the products in a legitimate research setting, such as a laboratory, research institution, or educational facility.",
      },
      {
        heading: "No Resale",
        body: "Products may not be resold, redistributed, repackaged, or relabeled without our prior written consent.",
      },
      {
        heading: "Verification & Refusal",
        body: "We may request information about the intended research use before fulfilling an order, and we reserve the right to decline or cancel orders that do not appear consistent with legitimate laboratory research.",
      },
      {
        heading: "Acknowledgement",
        body: "By placing an order, the buyer acknowledges and agrees to this Research Use Policy and the Compliance & Disclaimer in full.",
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    updated: "August 2026",
    sections: [
      {
        heading: "Agreement",
        body: "These Terms of Service govern your use of lab2o.com and your purchase of products from Lab2o. By using this site or placing an order, you agree to these terms.",
      },
      {
        heading: "Orders & Payment",
        body: "All orders are subject to acceptance and availability. Prices are listed in US dollars and are subject to change without notice. Orders are placed by email and paid by Zelle or ACH bank transfer. An order confirmation does not constitute acceptance; we may cancel orders in accordance with our policies.",
      },
      {
        heading: "Product Use Restrictions",
        body: "All products are sold strictly as laboratory research reagents, as described in our Research Use Policy and Compliance & Disclaimer, which are incorporated into these Terms by reference.",
      },
      {
        heading: "Shipping & Returns",
        body: "Shipping and returns are governed by our Shipping Policy and Refund Policy, incorporated into these Terms by reference.",
      },
      {
        heading: "Intellectual Property",
        body: "All content on this site, including text, graphics, logos, and images, is the property of Lab2o and may not be reproduced without permission.",
      },
      {
        heading: "Limitation of Liability",
        body: "To the maximum extent permitted by law, Lab2o shall not be liable for indirect, incidental, or consequential damages arising from the use of this site or the purchase or use of products. Our total liability for any claim shall not exceed the amount paid for the product giving rise to the claim.",
      },
      {
        heading: "Governing Law",
        body: "These Terms are governed by the laws of the State of North Dakota, without regard to conflict-of-law principles. Any dispute shall be brought in the state or federal courts located in North Dakota.",
      },
      {
        heading: "Contact",
        body: "Questions about these Terms may be directed to orders@lab2o.com.",
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "August 2026",
    sections: [
      {
        heading: "Information We Collect",
        body: "When you place an order or send an enquiry, we collect your name, email address, billing and shipping addresses, organisation name (if provided), and order details. Payment is arranged by Zelle or ACH bank transfer; we never see or store your payment card number.",
      },
      {
        heading: "How We Use Information",
        body: "We use your information to process and fulfil orders, communicate with you about your orders and enquiries, comply with legal and tax obligations, and improve our services. We do not use your information for unrelated marketing without your consent.",
      },
      {
        heading: "Sharing",
        body: "We share information only with the service providers necessary to operate our business: shipping carriers (delivery) and our email provider (order communications). We do not sell, rent, or trade your personal information.",
      },
      {
        heading: "Cookies & Analytics",
        body: "This site uses only the minimal cookies or storage required for core functionality, such as maintaining your cart. We do not use advertising or tracking cookies.",
      },
      {
        heading: "Data Retention & Security",
        body: "Order records are retained as required for legal, tax, and accounting purposes. We apply reasonable technical and organisational measures to protect your information.",
      },
      {
        heading: "Your Rights",
        body: "You may request access to, correction of, or deletion of your personal information by contacting us at orders@lab2o.com. We will respond within a reasonable timeframe, subject to legal retention requirements.",
      },
      {
        heading: "Contact",
        body: "Lab2o · orders@lab2o.com.",
      },
    ],
  },
};
